import React, { useState, useEffect } from 'react';

const CATEGORIES = [
  { value: 'alert', label: 'ALERT', color: 'text-red-400 border-red-800 bg-red-950/20' },
  { value: 'news', label: 'NEWS', color: 'text-[#C5A52A] border-[#C5A52A]/40 bg-[#C5A52A]/5' },
  { value: 'community', label: 'COMMUNITY', color: 'text-green-400 border-green-800 bg-green-950/20' },
];

const EMPTY = { title: '', body: '', link_url: '', link_label: 'Read More', category: 'news', is_active: true, expires_at: '' };

const AlertsTab = ({ api, toast }) => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const fetchAlerts = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/community-alerts');
      setAlerts(res.data?.alerts || []);
    } catch { toast?.error?.('Failed to load alerts'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAlerts(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post('/admin/community-alerts', {
        ...form,
        expires_at: form.expires_at || null
      });
      toast?.success?.('Alert posted');
      setForm(EMPTY);
      setShowForm(false);
      fetchAlerts();
    } catch (err) { toast?.error?.(err?.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const toggle = async (alert) => {
    try {
      await api.patch(`/admin/community-alerts/${alert.id}`, { is_active: !alert.is_active });
      fetchAlerts();
    } catch { toast?.error?.('Failed to update'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this alert?')) return;
    try {
      await api.delete(`/admin/community-alerts/${id}`);
      toast?.success?.('Alert deleted');
      fetchAlerts();
    } catch { toast?.error?.('Failed to delete'); }
  };

  const catStyle = (cat) => CATEGORIES.find(c => c.value === cat)?.color || '';

  return (
    <div className="space-y-6 font-mono">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white tracking-wider">COMMUNITY ALERTS</h2>
          <p className="text-gray-600 text-xs mt-1">Post news, alerts, and community notices to the dashboard</p>
        </div>
        <button
          onClick={() => setShowForm(v => !v)}
          className="bg-[#C5A52A] hover:bg-yellow-400 text-black text-xs px-4 py-2 font-bold transition-colors"
        >
          + NEW ALERT
        </button>
      </div>

      {showForm && (
        <form onSubmit={save} className="bg-[#0a0a0a] border border-[#2a2a2a] rounded p-5 space-y-4">
          <div className="flex gap-2 flex-wrap">
            {CATEGORIES.map(c => (
              <button key={c.value} type="button"
                onClick={() => setForm(f => ({ ...f, category: c.value }))}
                className={`text-xs px-3 py-1 border rounded transition-colors ${form.category === c.value ? c.color : 'text-gray-600 border-[#2a2a2a]'}`}>
                {c.label}
              </button>
            ))}
          </div>
          <input required maxLength={120} placeholder="Title *" value={form.title}
            onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
            className="w-full bg-[#111] border border-[#2a2a2a] text-white text-sm px-3 py-2 rounded" />
          <textarea maxLength={300} placeholder="Body (optional)" value={form.body}
            onChange={e => setForm(f => ({ ...f, body: e.target.value }))}
            className="w-full bg-[#111] border border-[#2a2a2a] text-white text-sm px-3 py-2 rounded h-16 resize-none" />
          <div className="grid grid-cols-2 gap-3">
            <input type="url" placeholder="Link URL (optional)" value={form.link_url}
              onChange={e => setForm(f => ({ ...f, link_url: e.target.value }))}
              className="w-full bg-[#111] border border-[#2a2a2a] text-white text-sm px-3 py-2 rounded" />
            <input placeholder="Link label" value={form.link_label}
              onChange={e => setForm(f => ({ ...f, link_label: e.target.value }))}
              className="w-full bg-[#111] border border-[#2a2a2a] text-white text-sm px-3 py-2 rounded" />
          </div>
          <div>
            <label className="text-[10px] text-gray-500 uppercase tracking-widest block mb-1">Expires at (optional)</label>
            <input type="datetime-local" value={form.expires_at}
              onChange={e => setForm(f => ({ ...f, expires_at: e.target.value }))}
              className="bg-[#111] border border-[#2a2a2a] text-white text-sm px-3 py-2 rounded" />
          </div>
          <div className="flex gap-3">
            <button type="submit" disabled={saving}
              className="bg-[#C5A52A] hover:bg-yellow-400 text-black text-xs px-5 py-2 font-bold disabled:opacity-50 transition-colors">
              {saving ? 'POSTING…' : 'POST'}
            </button>
            <button type="button" onClick={() => { setShowForm(false); setForm(EMPTY); }}
              className="text-gray-500 text-xs px-4 py-2 border border-[#2a2a2a] hover:text-white transition-colors">
              CANCEL
            </button>
          </div>
        </form>
      )}

      {loading && <p className="text-gray-600 text-xs">Loading…</p>}

      {!loading && alerts.length === 0 && (
        <p className="text-gray-700 text-xs">No alerts yet. Create one above.</p>
      )}

      <div className="space-y-2">
        {alerts.map(alert => (
          <div key={alert.id} className={`border rounded p-3 flex items-start gap-3 ${catStyle(alert.category)} ${!alert.is_active ? 'opacity-40' : ''}`}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                <span className="text-[9px] uppercase tracking-widest opacity-70">
                  {CATEGORIES.find(c => c.value === alert.category)?.label}
                </span>
                {!alert.is_active && <span className="text-[9px] text-gray-600">· INACTIVE</span>}
                {alert.expires_at && <span className="text-[9px] text-gray-600">· expires {new Date(alert.expires_at).toLocaleDateString()}</span>}
              </div>
              <p className="text-white text-sm">{alert.title}</p>
              {alert.body && <p className="text-xs opacity-70 mt-0.5">{alert.body}</p>}
              {alert.link_url && <a href={alert.link_url} target="_blank" rel="noopener noreferrer" className="text-[10px] underline opacity-60 hover:opacity-100">{alert.link_label}</a>}
            </div>
            <div className="flex gap-2 flex-shrink-0">
              <button onClick={() => toggle(alert)}
                className="text-[10px] border border-current px-2 py-1 opacity-70 hover:opacity-100 transition-opacity">
                {alert.is_active ? 'HIDE' : 'SHOW'}
              </button>
              <button onClick={() => remove(alert.id)}
                className="text-[10px] text-red-600 border border-red-900/50 px-2 py-1 hover:bg-red-950/30 transition-colors">
                DEL
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AlertsTab;
